import type { FilmTimeline } from "./transitions";

/**
 * Anivaze v4 — the SFX layer (ANIMATION-STACK §3.1). Sound is half of "editing
 * feel": a whoosh under each cut, a pop as a visual lands, a marker scratch
 * while the doodle hand sketches. Everything is SYNTHESIZED with WebAudio —
 * no audio files, no network, $0 — from a seeded PRNG (deterministic character),
 * and the event plan is a PURE function of the film timeline, so the live
 * preview and the exporter schedule identical sounds (preview === export).
 * Gains sit well under the voiceover (SFX season the mix, never lead it).
 */

export type SfxType = "whoosh" | "pop" | "scratch";
export interface SfxEvent { type: SfxType; at: number }

/** Pure: the sound plan for a film. Whoosh under every cut; a pop as each
 *  beat's visual settles; the doodle theme sketches, so its pop becomes a
 *  marker scratch at the draw-on start. */
export function planSfx(timeline: FilmTimeline, themeId: string): SfxEvent[] {
  const events: SfxEvent[] = [];
  const doodle = themeId === "doodle";
  for (const w of timeline.windows) {
    if (w.index > 0) events.push({ type: "whoosh", at: w.start });
    events.push({ type: doodle ? "scratch" : "pop", at: w.start + (doodle ? 0.15 : 0.35) });
  }
  return events.sort((a, b) => a.at - b.at);
}

// Seeded PRNG (mulberry32) → the noise buffer is identical on every render.
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let noiseCache: AudioBuffer | null = null;
function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  if (noiseCache && noiseCache.sampleRate === ctx.sampleRate) return noiseCache;
  const len = Math.floor(ctx.sampleRate * 1.5);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  const rnd = mulberry32(1337);
  for (let i = 0; i < len; i++) data[i] = rnd() * 2 - 1;
  noiseCache = buf;
  return buf;
}

/**
 * Schedule one SFX at absolute audio-clock time `when` (seconds), routed to
 * `dest`. `rate` slows envelopes to match the export pacing ramp.
 */
export function scheduleSfx(
  ctx: BaseAudioContext,
  dest: AudioNode,
  type: SfxType,
  when: number,
  rate = 1,
): void {
  const g = ctx.createGain();
  g.connect(dest);
  const t0 = Math.max(when, ctx.currentTime + 0.01);

  if (type === "whoosh") {
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx);
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.1;
    const dur = 0.28 / rate;
    bp.frequency.setValueAtTime(220, t0);
    bp.frequency.exponentialRampToValueAtTime(1900, t0 + dur * 0.7);
    bp.frequency.exponentialRampToValueAtTime(500, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.11, t0 + dur * 0.35);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(g);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  } else if (type === "pop") {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    const dur = 0.09 / rate;
    osc.frequency.setValueAtTime(640, t0);
    osc.frequency.exponentialRampToValueAtTime(290, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.16, t0 + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  } else {
    // scratch: three short granular noise strokes, like a marker on board.
    const strokes = 3;
    for (let i = 0; i < strokes; i++) {
      const src = ctx.createBufferSource();
      src.buffer = noiseBuffer(ctx);
      src.playbackRate.value = 1.6;
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.value = 1800;
      const sg = ctx.createGain();
      const s0 = t0 + (i * 0.34) / rate;
      const sd = 0.22 / rate;
      sg.gain.setValueAtTime(0.0001, s0);
      sg.gain.exponentialRampToValueAtTime(0.09, s0 + sd * 0.3);
      sg.gain.exponentialRampToValueAtTime(0.0001, s0 + sd);
      src.connect(hp).connect(sg).connect(g);
      g.gain.value = 1;
      src.start(s0, (i * 0.19) % 1);
      src.stop(s0 + sd + 0.02);
    }
  }
}
